import { ApiResponse, Tournament, TournamentCategory, TournamentFile, Match } from '@/types/api.types';
import apiService from './api.service';
import { cache } from '../utils/cache';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

export type TournamentStatus = 'DRAFT' | 'PUBLISHED' | 'IN_PROGRESS' | 'COMPLETED' | 'DELETED';

export interface TournamentFilter {
    status?: TournamentStatus;
    startDate?: string;
    endDate?: string;
    organizerId?: number;
    search?: string;
    region?: string;
}

export class TournamentService {
    static async getAll(filter: TournamentFilter = {}): Promise<Tournament[]> {
        // Создаем ключ кэша на основе фильтров
        const cacheKey = `tournaments_${JSON.stringify(filter)}`;

        return cache.getOrFetch(
            cacheKey,
            async () => {
                try {
                    const { data: response } = await apiService.get<ApiResponse<Tournament[]>>(
                        '/tournaments',
                        { params: filter }
                    );
                    return response.data;
                } catch (error) {
                    logger.error('Failed to fetch tournaments:', error);
                    throw error;
                }
            },
            5 * 60 * 1000 // 5 минут
        );
    }

    static async getById(id: number): Promise<Tournament> {
        const cacheKey = `tournament_${id}`;

        return cache.getOrFetch(
            cacheKey,
            async () => {
                try {
                    const { data: response } = await apiService.get<ApiResponse<Tournament>>(
                        `/tournaments/${id}`,
                        {
                            params: {
                                include: ['organizer', 'categories', 'files']
                            }
                        }
                    );

                    if (!response.data) {
                        throw new Error('Failed to fetch tournament data');
                    }

                    return response.data;
                } catch (error) {
                    logger.error(`Failed to fetch tournament ${id}:`, error);
                    throw error;
                }
            },
            5 * 60 * 1000 // 5 минут
        );
    }

    static async create(data: Partial<Tournament>): Promise<Tournament> {
        try {
            const { data: response } = await apiService.post<ApiResponse<Tournament>>(
                '/tournaments',
                data
            );

            // Инвалидируем кэш турниров
            cache.invalidatePattern('^tournaments_');

            return response.data;
        } catch (error) {
            logger.error('Failed to create tournament:', error);
            throw error;
        }
    }

    static async update(id: number, data: Partial<Tournament>): Promise<Tournament> {
        try {
            const { data: response } = await apiService.put<ApiResponse<Tournament>>(
                `/tournaments/${id}`,
                data
            );

            // Инвалидируем кэш для этого турнира и списка турниров
            cache.delete(`tournament_${id}`);
            cache.invalidatePattern('^tournaments_');

            return response.data;
        } catch (error) {
            logger.error(`Failed to update tournament ${id}:`, error);
            throw error;
        }
    }

    static async updateStreamLinks(id: number, streamLinks: Record<string, string> | null): Promise<Tournament> {
        try {
            const { data: response } = await apiService.patch<ApiResponse<Tournament>>(
                API_ENDPOINTS.TOURNAMENTS.STREAM_LINKS(id),
                { streamLinks },
                { headers: { 'Content-Type': 'application/json' } }
            );
            cache.delete(`tournament_${id}`);
            cache.invalidatePattern('^tournaments_');
            return response.data;
        } catch (error) {
            logger.error(`Failed to update stream links for tournament ${id}:`, error);
            throw error;
        }
    }

    static async delete(id: number): Promise<void> {
        try {
            await apiService.delete(`/tournaments/${id}`);

            // Инвалидируем кэш
            cache.delete(`tournament_${id}`);
            cache.invalidatePattern('^tournaments_');
        } catch (error) {
            logger.error(`Failed to delete tournament ${id}:`, error);
            throw error;
        }
    }

    static async getCategories(): Promise<TournamentCategory[]> {
        try {
            // Using the correct endpoint for categories
            // Логирование убрано для уменьшения шума в консоли
            const { data: response } = await apiService.get<ApiResponse<TournamentCategory[]>>(
                API_ENDPOINTS.CATEGORIES.BASE
            );
            return response.data;
        } catch (error) {
            logger.error('Failed to fetch all categories:', error);
            throw error;
        }
    }

    static async getTournamentCategories(tournamentId: number): Promise<TournamentCategory[]> {
        try {
            // Сначала используем dedicated endpoint GET /tournaments/:id/categories
            // Он возвращает { success: true, data: categories } с правильными id (tc.id) и categoryId (categories.id)
            try {
                const { data: categoriesResponse } = await apiService.get<{ success?: boolean; data?: TournamentCategory[] }>(
                    `/tournaments/${tournamentId}/categories`
                );
                const raw = categoriesResponse?.data ?? categoriesResponse;
                const categories = Array.isArray(raw) ? raw : [];
                if (categories.length > 0) {
                    return categories.map((cat: any) => ({
                        ...cat,
                        id: Number(cat.id),
                        categoryId: cat.categoryId != null ? Number(cat.categoryId) : (cat.id != null ? Number(cat.id) : undefined)
                    }));
                }
            } catch (categoriesEndpointError) {
                logger.warn('Dedicated categories endpoint failed, falling back to tournament include:', categoriesEndpointError);
            }

            // Fallback: tournament endpoint with include=categories
            const endpoint = API_ENDPOINTS.TOURNAMENTS.CATEGORIES(tournamentId);
            const { data: response } = await apiService.get<ApiResponse<Tournament>>(
                endpoint
            );

            if (!response.data) {
                logger.warn(`No tournament data found for tournament ${tournamentId}`);
                return [];
            }

            let categories: TournamentCategory[] = [];

            if (response.data.categories && Array.isArray(response.data.categories)) {
                categories = response.data.categories.map((cat: any) => ({
                    ...cat,
                    categoryId: cat.categoryId || cat.weightCategory?.id || cat.id
                })) as TournamentCategory[];
            } else if (response.data.tournamentCategories && Array.isArray(response.data.tournamentCategories) && response.data.tournamentCategories.length > 0) {
                try {
                    const categoryPromises = response.data.tournamentCategories.map(async (tc: any) => {
                        if (tc.weightCategory) {
                            return {
                                id: tc.id,
                                categoryId: tc.categoryId ?? tc.weightCategory.id,
                                tournamentId: tournamentId,
                                name: tc.weightCategory.name,
                                weightFrom: tc.weightCategory.weightFrom || 0,
                                weightTo: tc.weightCategory.weightTo || 999,
                                ranks: response.data.beltLevels || [],
                                minAge: tc.weightCategory.ageFrom || 0,
                                maxAge: tc.weightCategory.ageTo || 99,
                                currentParticipants: 0,
                                status: 'active'
                            } as TournamentCategory;
                        }
                        return null;
                    });
                    const resolvedCategories = await Promise.all(categoryPromises);
                    categories = resolvedCategories.filter(Boolean) as TournamentCategory[];
                } catch (conversionError) {
                    logger.error('Error converting tournamentCategories:', conversionError);
                }
            }

            return categories;
        } catch (error: any) {
            // More detailed error logging
            const errorDetails = {
                message: error?.message || 'Unknown error',
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                data: error?.response?.data,
                url: error?.config?.url,
                method: error?.config?.method
            };

            logger.error(`Failed to fetch categories for tournament ${tournamentId}:`, errorDetails);

            // Return empty array instead of throwing error
            return [];
        }
    }

    static async updateTournamentFiles(tournamentId: number, files: TournamentFile[]) {
        const response = await apiService.put<ApiResponse<Tournament>>(
            `/tournaments/${tournamentId}/files`,
            { files }
        );
        return response.data;
    }

    static async updateCategories(tournamentId: number, categories: Array<{
        tournamentId: number;
        categoryId: number;
        status: 'active' | 'inactive' | 'cancelled';
    }>): Promise<void> {
        try {
            await apiService.post<ApiResponse<void>>(
                `/tournaments/${tournamentId}/categories`,
                { categories }
            );
        } catch (error) {
            logger.error('Failed to update tournament categories:', {
                tournamentId,
                error
            });
            throw error;
        }
    }

    static async registerParticipant(tournamentId: number, registrationData: any) {
        try {

            const endpoint = API_ENDPOINTS.TOURNAMENTS.REGISTER(tournamentId);
            const response = await apiService.post<ApiResponse<any>>(
                endpoint,
                registrationData
            );

            return response.data;
        } catch (error) {
            logger.error(`Failed to register participant for tournament ${tournamentId}:`, error);
            throw error;
        }
    }

    static async getRegisteredParticipants(
        tournamentId: number,
        categoryId?: number,
        page?: number,
        limit?: number,
        clubId?: number
    ): Promise<{ data: any[]; pagination?: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean } }> {
        try {

            // Если указана пагинация, используем новый эндпоинт с пагинацией
            if (page !== undefined && limit !== undefined) {
                try {
                    const endpoint = API_ENDPOINTS.TOURNAMENTS.REGISTERED_PARTICIPANTS(tournamentId);
                    const params: any = { page, limit };
                    if (categoryId) {
                        params.categoryId = categoryId;
                    }
                    if (clubId !== undefined && clubId !== null) {
                        params.clubId = clubId;
                    }

                    const response = await apiService.get<ApiResponse<any[]>>(endpoint, { params });

                    if (response.data?.data && Array.isArray(response.data.data)) {
                        const participants = response.data.data;
                        const pagination = response.data.pagination;
                        return { data: participants, pagination };
                    }
                } catch (paginatedError) {
                    logger.warn(`Paginated endpoint failed:`, paginatedError);
                    // Fallback to non-paginated method
                }
            }

            // Собираем все результаты из разных источников (старый метод без пагинации)
            const allResults: any[] = [];

            // If categoryId is provided, use the specific endpoint for category participants
            if (categoryId) {
                try {
                    const endpoint = `/tournaments/${tournamentId}/categories/${categoryId}/participants`;

                    const response = await apiService.get<ApiResponse<any[]>>(endpoint);

                    if (response.data?.data && Array.isArray(response.data.data)) {
                        const participants = response.data.data;
                        return { data: participants }; // Return directly as this is the most accurate data
                    }
                } catch (categoryError) {
                    logger.warn(`Category-specific endpoint failed:`, categoryError);
                    // Continue with other methods if this fails
                }
            }

            // 1. Основной эндпоинт с опциональным параметром categoryId
            try {
                const endpoint = API_ENDPOINTS.TOURNAMENTS.REGISTERED_PARTICIPANTS(tournamentId);
                const params = categoryId ? { categoryId } : {};

                const response = await apiService.get<ApiResponse<any[]>>(endpoint, { params });

                if (response.data?.data && Array.isArray(response.data.data)) {
                    const participants = response.data.data;
                    allResults.push(...participants);
                }
            } catch (primaryError) {
                logger.warn(`Primary endpoint failed:`, primaryError);
            }

            // 2. Получение турнира с включенными участниками
            try {

                const tournamentResponse = await apiService.get<ApiResponse<any>>(
                    `/tournaments/${tournamentId}?include=participants`
                );

                if (tournamentResponse.data?.data?.participants &&
                    Array.isArray(tournamentResponse.data.data.participants)) {

                    const participants = tournamentResponse.data.data.participants;

                    // Преобразуем в ожидаемый формат
                    const formattedParticipants = participants.map((p: any) => ({
                        id: p.id,
                        participantId: p.userId || p.id,
                        tournamentId: Number(tournamentId),
                        categoryId: p.categoryId,
                        status: p.status || 'registered',
                        participant: p.user || p
                    }));

                    // Filter by category if needed
                    const filteredParticipants = categoryId
                        ? formattedParticipants.filter((p: any) => p.categoryId === categoryId)
                        : formattedParticipants;

                    allResults.push(...filteredParticipants);
                }
            } catch (includeError) {
                logger.warn(`Tournament include method failed:`, includeError);
            }

            // 3. Запрос к эндпоинту registered-participants
            try {

                const endpoint = `/tournaments/${tournamentId}/registered-participants`;
                const participantsResponse = await apiService.get<ApiResponse<any[]>>(endpoint);

                if (participantsResponse.data?.data && Array.isArray(participantsResponse.data.data)) {
                    const participants = participantsResponse.data.data;

                    // Фильтруем по категории, если указана
                    if (categoryId) {
                        const filteredParticipants = participants.filter((p: any) =>
                            p.categoryId === categoryId || p.category_id === categoryId
                        );
                        allResults.push(...filteredParticipants);
                    } else {
                        allResults.push(...participants);
                    }
                }
            } catch (registeredError) {
                logger.warn(`Registered participants endpoint failed:`, registeredError);
            }

            // 4. Запрос к эндпоинту получения участников
            try {

                const participantsEndpoint = API_ENDPOINTS.TOURNAMENTS.GET_PARTICIPANTS(tournamentId);
                const bulkResponse = await apiService.get<ApiResponse<any[]>>(participantsEndpoint);

                if (bulkResponse.data?.data && Array.isArray(bulkResponse.data.data)) {
                    const participants = bulkResponse.data.data;

                    // Filter by category if needed
                    const filteredParticipants = categoryId
                        ? participants.filter((p: any) => p.categoryId === categoryId)
                        : participants;

                    allResults.push(...filteredParticipants);
                }
            } catch (participantsError) {
                logger.warn(`Get participants endpoint failed:`, participantsError);
            }

            // Удаляем дубликаты по participantId/userId
            const uniqueParticipants = new Map<string, any>();

            allResults.forEach(participant => {
                const participantId = participant.participantId || participant.userId || participant.participant_id;
                if (participantId) {
                    uniqueParticipants.set(String(participantId), participant);
                }
            });

            const finalResults = Array.from(uniqueParticipants.values());

            return { data: finalResults };
        } catch (error) {
            logger.error(`Failed to fetch registered participants for tournament ${tournamentId}${categoryId ? ` and category ${categoryId}` : ''}:`, error);
            // Return empty array instead of throwing
            return { data: [] };
        }
    }

    // New method specifically for getting participants by category
    static async getCategoryParticipants(tournamentId: number, categoryId: number): Promise<any[]> {
        try {
            const tid = Number(tournamentId);
            const cid = Number(categoryId);
            const endpoint = `/tournaments/${tid}/categories/${cid}/participants`;

            const response = await apiService.get<ApiResponse<any[]>>(endpoint);


            if (response.data?.data && Array.isArray(response.data.data)) {
                const participants = response.data.data;
                return participants;
            }

            // If response.data is an array directly (some APIs return data directly)
            if (Array.isArray(response.data)) {
                return response.data;
            }

            // If the dedicated endpoint fails or returns no data, fall back to filtering
            const result = await this.getRegisteredParticipants(tournamentId, categoryId);
            const fallbackData = Array.isArray(result) ? result : result.data;
            return fallbackData || [];
        } catch (error: any) {
            logger.error(`Failed to fetch category participants for tournament ${tournamentId} and category ${categoryId}:`, {
                error,
                message: error?.message,
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                data: error?.response?.data
            });

            // Check if the error is a 404 (category not found)
            if (error?.response?.status === 404 || error?.response?.status === 500) {
                logger.warn(`Category ${categoryId} not found in tournament ${tournamentId}, returning empty array`);
                return [];
            }

            // Fall back to the general method with filtering
            try {
                const result = await this.getRegisteredParticipants(tournamentId, categoryId);
                const fallbackData = Array.isArray(result) ? result : result.data;
                return fallbackData || [];
            } catch (fallbackError) {
                logger.error('Fallback also failed:', fallbackError);
                return [];
            }
        }
    }

    static async getParticipantCount(tournamentId: number): Promise<number> {
        try {

            // Try a direct SQL count query first (more efficient)
            try {
                const endpoint = `/tournaments/${tournamentId}/participant-count`;

                const response = await apiService.get<ApiResponse<{ count: number }>>(endpoint);

                if (response.data?.data?.count !== undefined) {
                    const count = response.data.data.count;
                    return count;
                }
            } catch (directCountError) {
                logger.warn(`Direct count endpoint failed, falling back to participant list:`, directCountError);
            }

            // Use the getRegisteredParticipants method to get the actual count
            const result = await this.getRegisteredParticipants(tournamentId);
            const participants = Array.isArray(result) ? result : result.data;
            return participants.length;
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 401 || status === 500) {
                logger.warn(`Participant count unavailable (auth or server error) for tournament ${tournamentId}`);
            } else {
                logger.error(`Failed to fetch participant count for tournament ${tournamentId}:`, error);
            }
            return 0;
        }
    }

    /**
     * Автоназначение категорий: для участников с tp.categoryId = NULL подставляем users.categoryId (если категория есть в турнире).
     * @returns { updated: number }
     */
    static async syncParticipantCategories(tournamentId: number): Promise<{ updated: number }> {
        const { data: response } = await apiService.post<ApiResponse<{ updated: number }>>(
            API_ENDPOINTS.TOURNAMENTS.SYNC_PARTICIPANT_CATEGORIES(tournamentId)
        );
        return response?.data ?? { updated: 0 };
    }

    /**
     * Список клубов-участников турнира с количеством участников (для сайдбара «Участники»).
     * Доступно организаторам и админам.
     */
    static async getRegisteredClubs(tournamentId: number): Promise<Array<{ id: number; name: string; participantCount: number }>> {
        try {
            const endpoint = API_ENDPOINTS.TOURNAMENTS.REGISTERED_CLUBS(tournamentId);
            const { data: response } = await apiService.get<ApiResponse<any[]>>(endpoint);
            const list = response?.data ?? response;
            if (!Array.isArray(list)) return [];
            return list.map((c: any) => ({
                id: c.clubId ?? c.club?.id ?? c.id,
                name: c.club?.name ?? c.name ?? '—',
                participantCount: Number(c.participantCount ?? 0)
            }));
        } catch (error: any) {
            if (error?.response?.status === 403 || error?.response?.status === 401) {
                return [];
            }
            logger.error(`Failed to fetch registered clubs for tournament ${tournamentId}:`, error);
            return [];
        }
    }

    /**
     * Перенос всех боев категории на другой татами
     */
    static async moveCategoryToTatami(categoryId: number, newTatamiNumber: number): Promise<ApiResponse<any>> {
        try {
            const { data } = await apiService.put<ApiResponse<any>>(
                `/matches/category/${categoryId}/move-tatami`,
                { newTatamiNumber }
            );
            return data;
        } catch (error) {
            logger.error('Failed to move category to tatami:', error);
            throw error;
        }
    }

    static async generateBrackets(tournamentId: number, numTatami: number = 1, categoryId?: number): Promise<ApiResponse<any>> {
        try {
            if (categoryId) {
                // Логирование убрано для уменьшения шума в консоли
            }

            // Получаем информацию о турнире и участниках перед генерацией
            try {
                const tournament = await this.getById(tournamentId);
                console.log('🏆 Информация о турнире:', {
                    id: tournament.id,
                    name: tournament.name,
                    status: tournament.status
                });

                // Получаем список участников
                const participantsResult = await this.getRegisteredParticipants(tournamentId, undefined, 1, 1000);
                const participants = participantsResult.data || [];

                // Анализируем categoryId участников
                const participantsWithoutCategoryId = participants.filter((p: any) => !p.categoryId);

                if (participantsWithoutCategoryId.length > 0) {
                    console.warn('⚠️ Участники без categoryId:', {
                        count: participantsWithoutCategoryId.length,
                        sample: participantsWithoutCategoryId.slice(0, 5).map((p: any) => ({
                            id: p.id,
                            userId: p.participantId,
                            allKeys: Object.keys(p)
                        }))
                    });
                }

            } catch (infoError) {
                console.warn('⚠️ Не удалось получить дополнительную информацию о турнире:', infoError);
            }

            const endpoint = `/tournaments/${tournamentId}/generate-brackets`;
            const payload = categoryId ? { numTatami, categoryId } : { numTatami };

            const response = await apiService.post<ApiResponse<any>>(endpoint, payload);

            console.log('✅ Ответ сервера generateBrackets:', response.data);

            console.groupEnd();
            return response.data;
        } catch (error: any) {
            console.group('❌ [ERROR] Ошибка при генерации пулов');
            console.error('Ошибка:', error);
            console.error('Детали ошибки:', {
                message: error?.message,
                response: error?.response?.data,
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                config: {
                    url: error?.config?.url,
                    method: error?.config?.method,
                    data: error?.config?.data
                }
            });

            if (error?.response?.data) {
                console.error('Данные ответа сервера:', error.response.data);
            }

            console.groupEnd();
            logger.error(`Ошибка при формировании пулов для турнира ${tournamentId}:`, error);
            throw error;
        }
    }

    static async registerParticipants(tournamentId: number, registrations: any[]): Promise<any> {
        try {

            // Validate registrations data
            if (!Array.isArray(registrations) || registrations.length === 0) {
                logger.error('Invalid registrations data:', registrations);
                throw new Error('Invalid registrations data');
            }

            // Validate each registration
            registrations.forEach((reg) => {
                if (!reg.participantId || !reg.categoryId || !reg.tournamentId) {
                    logger.warn('Invalid registration data:', reg);
                }
            });

            // Try to use the bulk registration endpoint first
            try {
                const bulkEndpoint = API_ENDPOINTS.TOURNAMENTS.REGISTER_BULK(tournamentId);
                // Use the bulk endpoint for registering multiple participants

                // Add a longer timeout for this request
                const bulkResponse = await apiService.post(bulkEndpoint, registrations, {
                    timeout: 60000 // 60 seconds timeout
                });

                // Check if the response indicates success
                // Use type assertion to handle the response
                const responseData = bulkResponse.data as ApiResponse<any>;
                if (responseData?.success) {
                    // Add a delay to ensure the database has time to process the registrations
                    await new Promise(resolve => setTimeout(resolve, 1000));
                } else {
                    logger.warn('Bulk registration response does not indicate success');
                }

                return bulkResponse.data;
            } catch (bulkError: any) {
                logger.warn(`Bulk registration failed, trying individual registrations:`, bulkError);

                // If bulk registration fails, try registering participants one by one
                const successfulRegistrations = [];
                const failedRegistrations = [];

                for (const registration of registrations) {
                    try {
                        const endpoint = API_ENDPOINTS.TOURNAMENTS.REGISTER(tournamentId);

                        // The backend expects a different format for individual registration
                        const response = await apiService.post(endpoint, {
                            categoryId: Number(registration.categoryId),
                            participantId: Number(registration.participantId) // Pass the participant ID explicitly
                        }, {
                            timeout: 30000 // 30 seconds timeout
                        });

                        // Add a small delay between requests
                        await new Promise(resolve => setTimeout(resolve, 200));

                        successfulRegistrations.push({
                            ...registration,
                            response: response.data
                        });
                    } catch (individualError) {
                        logger.error(`Failed to register participant ${registration.participantId}:`, individualError);
                        failedRegistrations.push({
                            ...registration,
                            error: individualError
                        });
                    }
                }


                if (failedRegistrations.length > 0 && successfulRegistrations.length === 0) {
                    // If all registrations failed, throw an error
                    throw bulkError;
                }

                return {
                    success: true,
                    data: {
                        successful: successfulRegistrations,
                        failed: failedRegistrations
                    }
                };
            }
        } catch (error: any) {
            // More detailed error logging
            const errorDetails = {
                message: error?.message || 'Unknown error',
                status: error?.response?.status,
                statusText: error?.response?.statusText,
                data: error?.response?.data,
                url: error?.config?.url,
                method: error?.config?.method
            };

            logger.error(`Failed to register participants for tournament ${tournamentId}:`, errorDetails);
            throw error;
        }
    }

    static async getTournamentSchedule(tournamentId: number): Promise<ApiResponse<Match[]>> {
        try {
            const response = await apiService.get<ApiResponse<Match[]>>(`/tournaments/${tournamentId}/schedule`);
            return response.data;
        } catch (error) {
            logger.error(`Ошибка при получении расписания турнира ${tournamentId}:`, error);
            throw error;
        }
    }

    static async getTournamentMatches(tournamentId: number): Promise<Match[] | ApiResponse<Match[]>> {
        try {
            const endpoint = `/tournaments/${tournamentId}/matches`;

            const response = await apiService.get<any>(endpoint);


            let matchesData: Match[] = [];

            if (response.data) {
                if (Array.isArray(response.data)) {
                    matchesData = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    matchesData = response.data.data;
                } else {
                    // Пытаемся извлечь данные из неожиданного формата
                    if (typeof response.data === 'object' && response.data !== null) {
                        matchesData = response.data;
                    }
                }
            } else {
                logger.warn(`No data received from API`);
            }

            // Если есть матчи без данных об участниках, пытаемся загрузить их отдельно
            const matchesWithoutParticipants = matchesData.filter(match =>
                (match.participant1Id && !match.participant1) ||
                (match.participant2Id && !match.participant2)
            );

            if (matchesWithoutParticipants.length > 0) {
                logger.warn(`Found ${matchesWithoutParticipants.length} matches with missing participant data`);

                try {
                    // Собираем все ID участников, для которых нет данных
                    const missingParticipantIds = new Set<number>();

                    matchesWithoutParticipants.forEach(match => {
                        if (match.participant1Id && !match.participant1) {
                            missingParticipantIds.add(match.participant1Id);
                        }
                        if (match.participant2Id && !match.participant2) {
                            missingParticipantIds.add(match.participant2Id);
                        }
                    });

                    if (missingParticipantIds.size > 0) {

                        // Загружаем данные о пользователях по ID
                        const participantIds = Array.from(missingParticipantIds);

                        // Запрашиваем данные о пользователях с сервера через публичный эндпоинт
                        const usersResponse = await apiService.get<any>(`/users/public?ids=${participantIds.join(',')}`);

                        if (usersResponse.data && Array.isArray(usersResponse.data)) {
                            const users = usersResponse.data;

                            // Создаем карту пользователей для быстрого поиска
                            const userMap = new Map();
                            users.forEach(user => {
                                userMap.set(user.id, user);
                            });

                            // Добавляем данные об участниках в матчи
                            matchesData = matchesData.map((match: any) => {
                                if (match.participant1Id && !match.participant1) {
                                    const user = userMap.get(match.participant1Id);
                                    if (user) {
                                        match.participant1 = {
                                            id: user.id,
                                            firstName: user.firstName || 'Имя не указано',
                                            lastName: user.lastName || 'Фамилия не указана'
                                        };
                                    }
                                }

                                if (match.participant2Id && !match.participant2) {
                                    const user = userMap.get(match.participant2Id);
                                    if (user) {
                                        match.participant2 = {
                                            id: user.id,
                                            firstName: user.firstName || 'Имя не указано',
                                            lastName: user.lastName || 'Фамилия не указана'
                                        };
                                    }
                                }

                                return match;
                            });

                        } else {
                            logger.warn(`Failed to load user data or unexpected format:`, usersResponse.data);
                        }
                    }
                } catch (participantError) {
                    logger.error(`Error loading participant data:`, participantError);
                }
            }

            return matchesData;
        } catch (error: any) {
            logger.error(`Ошибка при получении матчей турнира ${tournamentId}:`, error);

            // Log more detailed error information
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                logger.error(`Error response status: ${error.response.status}`);
                logger.error(`Error response data:`, error.response.data);
                logger.error(`Error response headers:`, error.response.headers);
            } else if (error.request) {
                // The request was made but no response was received
                logger.error(`No response received:`, error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                logger.error(`Error message: ${error.message}`);
            }

            throw error;
        }
    }

    static async updateMatch(matchId: number, data: { winnerParticipantId?: number | null, status?: string }): Promise<any> {
        try {
            const endpoint = `/matches/${matchId}`;
            const response = await apiService.put(endpoint, data);
            return response.data;
        } catch (error) {
            logger.error(`Error updating match ${matchId}:`, error);
            throw error;
        }
    }

    // Generate Olympic bracket for a category
    static async generateOlympicBracket(tournamentId: number, categoryId: number): Promise<any> {
        try {

            const response = await apiService.post<ApiResponse<any>>(
                `/tournaments/${tournamentId}/categories/${categoryId}/bracket`
            );

            return response.data;
        } catch (error) {
            logger.error('Failed to generate Olympic bracket:', error);
            throw error;
        }
    }

    // Update match result
    static async updateMatchResult(tournamentId: number, matchId: number, winnerId: number): Promise<void> {
        try {

            await apiService.put<ApiResponse<void>>(
                `/tournaments/${tournamentId}/matches/${matchId}/result`,
                { winnerId }
            );

        } catch (error) {
            logger.error('Failed to update match result:', error);
            throw error;
        }
    }

    // Update participant category
    static async unregisterParticipant(tournamentId: number, participantId: number): Promise<void> {
        try {
            await apiService.post(`/participants/${participantId}/unregister/${tournamentId}`, { tournamentId });
        } catch (error) {
            logger.error(`Failed to unregister participant ${participantId} from tournament ${tournamentId}:`, error);
            throw error;
        }
    }

    /**
     * Снять всю команду с турнира (организатором или менеджером своей команды)
     * @param reformBrackets — переформировать сетку для затронутых категорий (по умолчанию true)
     */
    static async unregisterClub(
        tournamentId: number,
        clubId: number,
        options?: { reformBrackets?: boolean }
    ): Promise<{ removedParticipants: number; bracketsReformed: number; categoriesNeedingReform: number }> {
        try {
            const params = new URLSearchParams();
            if (options?.reformBrackets === true) {
                params.set('reformBrackets', 'true');
            }
            const query = params.toString();
            const url = `/tournaments/${tournamentId}/clubs/${clubId}/unregister${query ? `?${query}` : ''}`;
            const { data: response } = await apiService.delete<{
                success: boolean;
                data: { removedParticipants: number; bracketsReformed?: number; affectedCategoriesCount?: number };
            }>(url);
            return {
                removedParticipants: response?.data?.removedParticipants ?? 0,
                bracketsReformed: response?.data?.bracketsReformed ?? 0,
                categoriesNeedingReform: response?.data?.affectedCategoriesCount ?? 0
            };
        } catch (error) {
            logger.error(`Failed to unregister club ${clubId} from tournament ${tournamentId}:`, error);
            throw error;
        }
    }

    static async updateParticipantCategory(
        tournamentId: number,
        participantId: number,
        categoryId: number
    ): Promise<{ categoriesNeedingReform: number }> {
        try {
            const endpoint = API_ENDPOINTS.TOURNAMENTS.UPDATE_PARTICIPANT_CATEGORY(tournamentId, participantId);
            const res = await apiService.put<ApiResponse<unknown> & { categoriesNeedingReform?: number }>(
                endpoint,
                { categoryId }
            );
            const body = (res as { data?: { categoriesNeedingReform?: number } })?.data;
            return { categoriesNeedingReform: body?.categoriesNeedingReform ?? 0 };
        } catch (error) {
            logger.error('Failed to update participant category:', error);
            throw error;
        }
    }

    /**
     * Получить список уникальных регионов из клубов, у которых есть турниры
     */
    static async getRegions(): Promise<string[]> {
        try {
            const { data: response } = await apiService.get<ApiResponse<string[]>>(
                API_ENDPOINTS.TOURNAMENTS.REGIONS
            );
            return response.data || [];
        } catch (error) {
            logger.error('Failed to fetch regions:', error);
            return [];
        }
    }

    static async publishBrackets(tournamentId: number): Promise<void> {
        try {
            await apiService.post<ApiResponse<{ tournamentId: number; bracketsPublished: boolean }>>(
                `/tournaments/${tournamentId}/brackets/publish`
            );
        } catch (error) {
            logger.error('Failed to publish brackets:', error);
            throw error;
        }
    }

    /** Переформирование PDF сеток (без смены флага публикации). Генерация может занять 10–30 сек. */
    static async regenerateBracketsPdf(tournamentId: number): Promise<void> {
        try {
            await apiService.post<ApiResponse<{ tournamentId: number }>>(
                `/tournaments/${tournamentId}/brackets/regenerate-pdf`
            );
        } catch (error) {
            logger.error('Failed to regenerate brackets PDF:', error);
            throw error;
        }
    }

    /** Переформировать сетки только для категорий с изменённым составом (bracketNeedsReform). */
    static async reformBrackets(tournamentId: number): Promise<{ reformedCount: number }> {
        const res = await apiService.post<ApiResponse<{ tournamentId: number; reformedCount: number }>>(
            `/tournaments/${tournamentId}/brackets/reform`
        );
        const payload = (res as any)?.data?.data ?? (res as any)?.data;
        return { reformedCount: payload?.reformedCount ?? 0 };
    }

    /**
     * Единый метод экспорта PDF сеток турнира: по списку категорий строится один PDF со всеми их сетками.
     * Порядок категорий в PDF совпадает с порядком categoryIds.
     * @param tournamentId — ID турнира
     * @param categoryIds — список ID категорий для выгрузки (пустой массив = все категории с матчами)
     * @returns Blob PDF для скачивания
     */
    static async exportBracketsPdf(
        tournamentId: number,
        categoryIds: number[]
    ): Promise<Blob> {
        const body: { categoryIds?: number[]; format: 'pdf' } = { format: 'pdf' };
        if (categoryIds.length > 0) body.categoryIds = categoryIds;

        try {
            const response = await apiService.post<Blob>(
                `/tournaments/${tournamentId}/brackets/export`,
                body,
                { responseType: 'blob' }
            );
            return (response as any).data as Blob;
        } catch (err: any) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            let message = status === 500 ? 'Ошибка сервера при экспорте PDF' : 'Ошибка при экспорте PDF';
            try {
                if (data != null) {
                    if (typeof data === 'object' && 'message' in data && typeof (data as { message?: string }).message === 'string') {
                        message = (data as { message: string }).message;
                    } else if (typeof data.text === 'function') {
                        const text = await data.text();
                        const json = JSON.parse(text || '{}');
                        if (json?.message) message = json.message;
                    } else if (typeof data === 'string') {
                        const json = JSON.parse(data || '{}');
                        if (json?.message) message = json.message;
                    }
                }
            } catch {
                // оставляем message по умолчанию
            }
            if (status === 400 || status === 500) {
                throw new Error(message);
            }
            throw err;
        }
    }

    /**
     * Скачать PDF сеток турнира по списку категорий (один файл).
     */
    static async downloadBracketsPdf(
        tournamentId: number,
        categoryIds: number[],
        fileName: string
    ): Promise<void> {
        const blob = await TournamentService.exportBracketsPdf(tournamentId, categoryIds);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.replace(/[<>:"/\\|?*]/g, '_');
        a.click();
        URL.revokeObjectURL(url);
    }

    /**
     * Универсальный экспорт сеток: список категорий → HTML или PDF (для внутреннего использования).
     * @param tournamentId — ID турнира
     * @param options — categoryIds: только эти категории (если не задано — все с матчами); format: 'pdf' | 'html'
     * @returns при format === 'html' — { html: string }; иначе — Blob (PDF для скачивания)
     */
    static async exportBrackets(
        tournamentId: number,
        options: { categoryIds?: number[]; format?: 'pdf' | 'html' } = {}
    ): Promise<Blob | { html: string }> {
        const { categoryIds, format = 'pdf' } = options;
        const body: { categoryIds?: number[]; format?: 'pdf' | 'html' } = { format };
        if (categoryIds?.length) body.categoryIds = categoryIds;

        if (format === 'html') {
            const res = await apiService.post<{ success: boolean; html: string }>(
                `/tournaments/${tournamentId}/brackets/export`,
                body
            );
            const html = (res as any)?.data?.html;
            if (html == null) throw new Error('Нет HTML в ответе');
            return { html };
        }

        return TournamentService.exportBracketsPdf(tournamentId, categoryIds ?? []);
    }

    /**
     * Update participant seeding order for a category
     */
    static async updateParticipantSeedingOrder(
        tournamentId: number,
        categoryId: number,
        participantIds: number[]
    ): Promise<void> {
        try {
            await apiService.put<ApiResponse<void>>(
                `/tournaments/${tournamentId}/categories/${categoryId}/participants/seeding-order`,
                { participantIds }
            );
        } catch (error) {
            logger.error('Failed to update participant seeding order:', error);
            throw error;
        }
    }

    /**
     * Update participant rank
     */
    static async updateParticipantRank(
        tournamentId: number,
        participantId: number,
        rank: number
    ): Promise<void> {
        try {
            await apiService.put<ApiResponse<void>>(
                `/tournaments/${tournamentId}/participants/${participantId}/rank`,
                { rank }
            );
        } catch (error) {
            logger.error('Failed to update participant rank:', error);
            throw error;
        }
    }

    /**
     * Update participant active status
     */
    static async updateParticipantActive(
        tournamentId: number,
        participantId: number,
        active: boolean
    ): Promise<void> {
        try {
            await apiService.put<ApiResponse<void>>(
                `/tournaments/${tournamentId}/participants/${participantId}/active`,
                { active }
            );
        } catch (error) {
            logger.error('Failed to update participant active status:', error);
            throw error;
        }
    }
}

export default TournamentService;